import { FC, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Tab, Tabs } from '@hopskipdrive/react-components';
import styled from 'styled-components';

import { useAuthQuery } from '../../auth/hooks/useAuthQuery';
import { actions, subjects, useAbility } from '../../contexts/AbilityContext';
import { useFlags } from '../../contexts/LDContext';
import { ActionBar } from '../../shared/components';
import { useNavigation } from '../../shared/hooks/useNavigation';
import { trackFullStoryEvent } from '../../vendor/features/FullStory/FullStory.service';
import SettingsForm from '../features/SettingsForm';

import { OrganizerList } from './OrganizerList';

const Layout: FC = () => {
  const { navigateTo } = useNavigation();
  const { enableOnsiteStaff } = useAuthQuery();
  const { billingDocumentsEnabled } = useFlags();
  const ability = useAbility();
  const location = useLocation();

  const CURRENT_TAB = location.pathname.split('/')[2];
  const showBillingDocuments = billingDocumentsEnabled && ability.can(actions.read, subjects.billingDocument);
  const availableTabs = [
    'account_details',
    'organizers',
    'invoice_groups',
    enableOnsiteStaff && 'onsite_staff',
    showBillingDocuments && 'billing_documents',
  ];

  useEffect(() => {
    trackFullStoryEvent('settings_viewed', { settings_section: CURRENT_TAB });
  }, [CURRENT_TAB]);

  // MUI skips onChange for the selected tab; reactivation must still return nested URLs to the section root.
  return (
    <StyledGrid>
      <div style={{ gridArea: 'header', position: 'sticky', top: 0, zIndex: 10, marginBottom: '1.5rem' }}>
        <ActionBar>
          <ActionBar.Title title="Account Settings" />
        </ActionBar>
      </div>
      <div style={{ gridArea: 'tabs', alignContent: 'center', minWidth: 0 }}>
        <Tabs
          value={availableTabs.includes(CURRENT_TAB) ? CURRENT_TAB : false}
          data-testid="tabs-nav"
          aria-label="Account Settings"
          showIndicator
          variant="scrollable"
          scrollButtons="auto"
          style={{ minWidth: 0 }}
          onChange={(_, tab: string) => navigateTo(`/settings/${tab}`)}
        >
          <Tab
            label={'Account Details'}
            value={'account_details'}
            onClick={() => CURRENT_TAB === 'account_details' && navigateTo('/settings/account_details')}
            sx={{ marginLeft: 0 }}
          />
          <Tab
            label={'Organizers'}
            value={'organizers'}
            onClick={() => CURRENT_TAB === 'organizers' && navigateTo('/settings/organizers')}
          />
          {enableOnsiteStaff && (
            <Tab
              label={'Onsite Staff'}
              value={'onsite_staff'}
              onClick={() => CURRENT_TAB === 'onsite_staff' && navigateTo('/settings/onsite_staff')}
            />
          )}
          <Tab
            label={'Invoice Groups'}
            value={'invoice_groups'}
            onClick={() => CURRENT_TAB === 'invoice_groups' && navigateTo('/settings/invoice_groups')}
          />
          {showBillingDocuments && (
            <Tab
              label={'Billing Documents'}
              value={'billing_documents'}
              onClick={() => CURRENT_TAB === 'billing_documents' && navigateTo('/settings/billing_documents')}
            />
          )}
        </Tabs>
      </div>
      <Outlet />
    </StyledGrid>
  );
};

const OriginalSettingsPage = () => {
  return (
    <>
      <ActionBar>
        <ActionBar.Title title="Account Settings" />
      </ActionBar>
      <SettingsForm />
    </>
  );
};

const Settings = () => {
  const { isAdminOrOrganizerUser } = useAuthQuery();

  return isAdminOrOrganizerUser ? <Layout /> : <OriginalSettingsPage />;
};

export default Settings;
export { SettingsForm, OrganizerList };
const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16rem;
  grid-template-areas: 'header header' 'tabs actions' 'filters filters' 'content content';

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'header'
      'tabs'
      'actions'
      'filters'
      'content';

    [data-testid='settings-actions'],
    [data-testid='organizer-actions'],
    [data-testid='onsite-actions'] {
      margin-top: 16px;
    }

    form {
      [data-settings-column] {
        width: 100%;
        max-width: unset;
        flex-basis: 100%;
        flex-grow: 1;
      }

      [data-settings-divider] {
        display: none;
      }

      [data-settings-explanation] {
        margin-top: 16px;
      }
    }
  }
`;
